import { FC, useEffect, useState, MouseEvent } from "react";
import { styled } from "@mui/material/styles";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import {
    Box,
    CircularProgress,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    tableCellClasses,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";

import { AdminLayout } from "../../../../components/layouts/admin-layout/admin-layout";
import { CustomButton } from "../../../../components";
import { Colors } from "../../../../configs";
import { AddCashDrawerReasonDrawer } from "../../../../components/admin/add-cash-drawer-reasons-drawer/AddCashDrawerReasonDrawer";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import {
    deleteCashDrawerReasonAction,
    getAllCashDrawerReasonsAction,
} from "../../../../redux/actions/cashDrawerReasonsAction";
import { useSnackbarContext } from "../../../../providers/SnackbarProvider";

interface ICashDrawerReasonParams {
    id: number;
    reason: string;
    type: string;
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        fontWeight: "bold",
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
        backgroundColor: theme.palette.action.hover,
    },
    "&:last-child td, &:last-child th": {
        border: 0,
    },
}));

export const CashDrawerReasonsPage: FC = () => {
    const dispatch = useAppDispatch();
    const {
        cashDrawerReasons,
        cashDrawerReasonsLoading,
        addCashDrawerReasonLoading,
        updateCashDrawerReasonLoading,
        deleteCashDrawerReasonLoading,
    } = useAppSelector((state) => state.cashDrawerReason);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedReason, setSelectedReason] = useState<ICashDrawerReasonParams | undefined>(undefined);
    const [openDrawer, setOpenDrawer] = useState(false);

    const snackBar = useSnackbarContext();

    useEffect(() => {
        dispatch(getAllCashDrawerReasonsAction());
    }, []);

    const handleClick = (event: MouseEvent<HTMLButtonElement>, reason: ICashDrawerReasonParams) => {
        setAnchorEl(event.currentTarget);
        setSelectedReason(reason);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleDeleteReason = async () => {
        if (!selectedReason) return;

        try {
            await dispatch(
                deleteCashDrawerReasonAction({
                    merchantId: 1,
                    storeId: 1,
                    cashDrawerReasonId: selectedReason.id,
                })
            ).unwrap();

            snackBar.showSnackbar("Cash Drawer Reason deleted successfully!", "success");
            dispatch(getAllCashDrawerReasonsAction());
            handleClose();
        } catch (error) {
            snackBar.showSnackbar("Failed to delete reason. Please try again.", "error");
        }
    };

    return (
        <AdminLayout>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "3rem",
                    height: "57.5rem",
                    marginTop: "4.357rem",
                    paddingInline: "5rem",
                    width: "100%",
                }}
            >
                <AddCashDrawerReasonDrawer
                    open={openDrawer}
                    toggleDrawer={() => setOpenDrawer(false)}
                    populateFor={selectedReason}
                />

                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="h4" sx={{ color: Colors.TEXT_GRAY_DARK }}>
                        Cash Drawer Reasons
                    </Typography>
                    <CustomButton
                        sx={{ width: "12rem", height: "3rem" }}
                        onClick={() => {
                            setSelectedReason(undefined);
                            setOpenDrawer(true);
                        }}
                        disabled={
                            cashDrawerReasonsLoading ||
                            addCashDrawerReasonLoading ||
                            updateCashDrawerReasonLoading ||
                            deleteCashDrawerReasonLoading
                        }
                    >
                        {cashDrawerReasonsLoading ||
                            addCashDrawerReasonLoading ||
                            deleteCashDrawerReasonLoading ||
                            updateCashDrawerReasonLoading ? (
                            <CircularProgress size={27} color="inherit" />
                        ) : (
                            "Add Reason"
                        )}
                    </CustomButton>
                </Box>

                <Box
                    sx={{
                        maxHeight: "51rem",
                        width: "100%",
                        overflowY: "auto",
                        "&::-webkit-scrollbar": { width: "0.5rem" },
                        "&::-webkit-scrollbar-thumb": { background: "#888" },
                    }}
                >
                    <Table sx={{ width: "100%" }} aria-label="customized table">
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>Reason</StyledTableCell>
                                <StyledTableCell>Type</StyledTableCell>
                                <StyledTableCell>Action</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(cashDrawerReasonsLoading ||
                                addCashDrawerReasonLoading ||
                                updateCashDrawerReasonLoading ||
                                deleteCashDrawerReasonLoading) && (
                                    <StyledTableRow>
                                        <StyledTableCell colSpan={3} align="center">
                                            <CircularProgress size={27} color="inherit" />
                                        </StyledTableCell>
                                    </StyledTableRow>
                                )}
                            {!(
                                cashDrawerReasonsLoading ||
                                addCashDrawerReasonLoading ||
                                updateCashDrawerReasonLoading ||
                                deleteCashDrawerReasonLoading
                            ) &&
                                cashDrawerReasons?.map((row) => (
                                    <StyledTableRow key={row.id}>
                                        <StyledTableCell sx={{ width: "60%" }}>
                                            {row.reason}
                                        </StyledTableCell>
                                        <StyledTableCell sx={{ width: "20%" }}>
                                            {row.type === 'ADD' ? 'Add Cash' : 'Remove Cash'}
                                        </StyledTableCell>
                                        <StyledTableCell sx={{ width: "20%" }}>
                                            <IconButton onClick={(e) => handleClick(e, row)}>
                                                <MoreHorizIcon sx={{ fontSize: "2rem" }} color="primary" />
                                            </IconButton>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ))}
                        </TableBody>
                    </Table>

                    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
                        <MenuItem
                            onClick={() => {
                                setOpenDrawer(true);
                                handleClose();
                            }}
                        >
                            <ListItemIcon>
                                <EditIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Edit Reason</ListItemText>
                        </MenuItem>

                        <MenuItem onClick={handleDeleteReason}>
                            <ListItemIcon>
                                <DeleteIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Delete Reason</ListItemText>
                        </MenuItem>
                    </Menu>
                </Box>
            </Box>
        </AdminLayout>
    );
};
